import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import styles from '../priestSeniority/priestSeniority.module.scss';
import tablestyles from '../../devoteeService/devoteeService.module.scss';
import { apiRequest, catchErrorMessageHandler } from '../../../infrastructure/backendService';
import ErrorPopup from '../../common/ErrorPopup';
import toast, { Toaster } from 'react-hot-toast';
import constants from '../../../config/constants.json';
import apiPath from '../../../config/apiPaths.json';
import Pagination from '../../common/pagination/pagination';
import SuccessPopup from '../../devotee/donation/successPopup';
import LOCALE from '../../../config/en-us.json';
import MenuIcon from "@mui/icons-material/Menu";

interface Priest {
  id: string;
  seniority: number;
  username: string;
  priestName: string;
}

const PriestSeniority = () => {
  const [priestSeniorityDetails, setPriestSeniorityDetails] = useState<
    Priest[]
  >([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleDragStart = (e: any, index: any) => {
    e.dataTransfer.setData("draggedItemIndex", index);
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
  };

    const handleDrop = (e: React.DragEvent<HTMLTableRowElement>, dropIndex: number) => {
        const draggedItemIndex = parseInt(e.dataTransfer.getData('draggedItemIndex'), 10);
        if (draggedItemIndex === dropIndex) return;

        const items = Array.from(priestSeniorityDetails);
        const draggedItem = items[draggedItemIndex];
        const dropItem = items[dropIndex];

        // Swap the seniority values
        const draggedItemSeniority = draggedItem.seniority;
        draggedItem.seniority = dropItem.seniority;
        dropItem.seniority = draggedItemSeniority;

        setPriestSeniorityDetails(items);

        // Update the API with the new seniority values for both items
        updatePoojaDetailsApiCall(draggedItem.username, draggedItem.seniority);
        updatePoojaDetailsApiCall(dropItem.username, dropItem.seniority);
    };
    const updatePoojaDetailsApiCall = (username: string, adjustedDropIndex: number) => {
        const reqBody = {
            username: username,
            seniority: adjustedDropIndex
        };
        apiRequest({
            method: 'PUT',
            endpoint: `${apiPath.priestSeniority}/${username}`,
            body: JSON.stringify(reqBody),
            includeToken: true,
        })
            .then((res) => {
                const successMessage = LOCALE.PRIEST_SENIORITY_CHANGED;
                toast.custom(
                    <SuccessPopup
                        headerMessage="Details Update"
                        showMessage={successMessage}
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                    />,
                    {
                        duration: constants.TOAST_POPUP_DURATION,
                    },
                );

                // After successful API call, fetch updated data and update the state
                apiRequest({
                    method: "GET",
                    endpoint: apiPath.priestSeniority,
                    includeToken: true
                })
                    .then((res) => {
                        setTotalRecords(res.data.totalElements);
                        setPriestSeniorityDetails(res.data.content);
                    })
                    .catch((err) => {
                        console.log(err);
                        toast.custom(
                            <ErrorPopup
                                showMessage={catchErrorMessageHandler(err)}
                                isOpen={isOpen}
                                setIsOpen={setIsOpen}
                            />,
                            {
                                duration: constants.TOAST_POPUP_DURATION,
                            }
                        );
                    });
            })
            .catch((err) => {
                console.log(err, "err")
            });
    };


    useEffect(() => {
        apiRequest({
            method: 'GET',
            endpoint: apiPath.priestSeniority,
            includeToken: true,
        })
            .then((res) => {
                setTotalRecords(res.data.totalElements);
                setPriestSeniorityDetails(res.data.content);
            })
            .catch((err) => {
                console.log(err);
                toast.custom(
                    <ErrorPopup
                        showMessage={catchErrorMessageHandler(err)}
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                    />,
                    {
                        duration: constants.TOAST_POPUP_DURATION,
                    },
                );
            });
    }, []);

  const priestSeniorityPage = () => {
    return (
      <div>
        <Typography className={styles.subTitle}>Priest Seniority</Typography>
        <div style={{ marginBottom: "15px", marginTop: "15px" }}>
          <Typography className={styles.description}>
            You can change the seniority of the priests using the following
            form. Changes to the seniority order of priests are normally needed
            when there is an addition or deletion of a name to the list of the
            priests.
          </Typography>
          <Typography
            className={styles.description}
            style={{ marginTop: "5px" }}
          >
            To change the seniority order for a particular priest, drag the row
            to the desired position.
          </Typography>
        </div>
        <table
          className={tablestyles.tableContainer}
          style={{ maxWidth: "700px" }}
        >
          <thead>
            <tr className={tablestyles.headerTableRow}>
              <th></th>
              <th className={tablestyles.tableHorizontal}>Seniority</th>
              <th className={tablestyles.tableHorizontal}>Name</th>
            </tr>
          </thead>
          {priestSeniorityDetails.map((priest, index, data) => (
            <tr
              key={priest?.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              className={tablestyles.tableRow} // Add appropriate styles for the row
            >
              <td
                className={tablestyles.tableDiagonal}
                style={{ padding: "8px 8px 8px 16px", width: "24px" }}
              >
                <MenuIcon style={{ cursor: "grab" }} />
              </td>
              <td className={tablestyles.tableDiagonal}>{priest.seniority}</td>
              <td className={tablestyles.tableDiagonal}>
                {priest.priestName} - ({priest?.username})
              </td>
            </tr>
          ))}
        </table>
        {totalRecords > 20 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "end",
              marginRight: "10px",
              marginTop: "3px",
            }}
          >
            <Pagination
              items={totalRecords}
              itemsPerPage={20}
              setPageNumber={setPageNumber}
              pageNumber={pageNumber}
            />
          </div>
        ) : (
          ""
        )}
      </div>
    );
  };

  return (
    <div className={styles.mainContainer}>
      <Toaster />
      {priestSeniorityPage()}
    </div>
  );
};

export default PriestSeniority;
